import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ENVIRONMENT_LABELS,
  ENVIRONMENT_TOKENS,
  SEVERITY_LABELS,
  type ConnectionDto,
  type Environment,
  type ImpactSeverity,
  type TypedTokenKind,
} from '@visualitics-toolkit/shared';

import { Badge, Button, CopyButton, EnvDot, Input, Select, Spinner } from '../atoms';
import {
  useTargetBrands,
  useTargetBusinessUnits,
  useTargetCategories,
  useTargetOrganizations,
  useTargetRetailers,
  useTargetUsers,
} from '../../lib/queries';

export { AppVersionFooter } from './AppVersionFooter';

const SEVERITY_TONE: Record<ImpactSeverity, 'accent' | 'success' | 'warning' | 'danger'> = {
  'read-only': 'accent',
  reversible: 'success',
  destructive: 'warning',
  irreversible: 'danger',
};

export function SeverityBadge({ severity }: { severity: ImpactSeverity }): ReactNode {
  return <Badge tone={SEVERITY_TONE[severity]}>{SEVERITY_LABELS[severity]}</Badge>;
}

/** Faixa superior do shell: a cor é o lembrete constante de onde o operador está (C2). */
export function EnvBanner({
  connection,
  onSwitch,
}: {
  connection: ConnectionDto | null;
  onSwitch: () => void;
}): ReactNode {
  if (!connection) {
    return (
      <div className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-2 text-sm">
        <span className="text-muted">Nenhuma conexão ativa.</span>
        <Button variant="primary" onClick={onSwitch}>
          Configurar conexão
        </Button>
      </div>
    );
  }

  return (
    <div
      data-env={connection.environment}
      data-testid="env-banner"
      className="flex items-center justify-between border-b-2 border-env bg-surface-raised px-4 py-2 text-sm"
    >
      <div className="flex items-center gap-3">
        <EnvDot environment={connection.environment} />
        <span className="font-semibold uppercase tracking-wide text-env">
          {ENVIRONMENT_LABELS[connection.environment]}
        </span>
        <span className="text-muted">
          {connection.name} · {connection.host}:{connection.port}/{connection.database}
        </span>
      </div>
      <Button variant="ghost" onClick={onSwitch}>
        trocar
      </Button>
    </div>
  );
}

/** Estimado vs real lado a lado — visibilidade de tempo é princípio da constituição (C8). */
export function DurationPair({
  estimatedMs,
  actualMs,
}: {
  estimatedMs?: number | null;
  actualMs?: number | null;
}): ReactNode {
  return (
    <span className="font-mono text-xs text-muted">
      est. {formatDuration(estimatedMs)} · real {formatDuration(actualMs)}
    </span>
  );
}

export function formatDuration(ms?: number | null): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  return `${minutes}m ${Math.round((ms % 60_000) / 1000)}s`;
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

export function formatBytes(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function SqlPreview({
  sql,
  label,
  params,
}: {
  sql: string;
  label?: string;
  params?: unknown[];
}): ReactNode {
  return (
    <div className="rounded-md border border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="text-xs font-medium text-muted">{label ?? 'SQL'}</span>
        <CopyButton value={sql} />
      </div>
      <pre className="max-h-64 overflow-auto px-3 py-2 font-mono text-xs leading-relaxed text-content">
        {sql}
      </pre>
      {params && params.length > 0 && (
        <div className="border-t border-border px-3 py-1.5 font-mono text-[11px] text-muted">
          params: {JSON.stringify(params)}
        </div>
      )}
    </div>
  );
}

export function JsonExportButton({ data, filename }: { data: unknown; filename: string }): ReactNode {
  return (
    <Button
      onClick={() => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
      }}
    >
      Exportar JSON
    </Button>
  );
}

/**
 * Campo do token de dupla validação. O valor esperado é MOSTRADO — o atrito é digitar,
 * não adivinhar; e a verificação real acontece no servidor (spec/08 §4).
 */
export function ConfirmTokenField({
  kind,
  hint,
  value,
  onChange,
  expected,
}: {
  kind: TypedTokenKind;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  expected?: string | null;
}): ReactNode {
  const matched = Boolean(expected && value.trim() === expected.trim());

  return (
    <div className="rounded-md border border-severity-destructive/50 bg-surface p-3">
      <label className="field-label" htmlFor={`token-${kind}`}>
        {hint}
      </label>
      <Input
        id={`token-${kind}`}
        data-testid="confirm-token"
        value={value}
        autoComplete="off"
        spellCheck={false}
        placeholder={expected ?? ''}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={`token-${kind}-help`}
      />
      <p id={`token-${kind}-help`} className="field-help">
        {expected ? (
          <>
            Digite exatamente: <code className="font-mono text-content">{expected}</code>{' '}
            {matched && <span className="text-severity-reversible">✓ confere</span>}
          </>
        ) : (
          'O valor esperado será validado pelo servidor.'
        )}
      </p>
    </div>
  );
}

export function ConnectionSelect({
  connections,
  value,
  onChange,
}: {
  connections: ConnectionDto[];
  value?: string;
  onChange: (id: string) => void;
}): ReactNode {
  return (
    <Select value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
      <option value="" disabled>
        Selecione a conexão
      </option>
      {connections.map((connection) => (
        <option key={connection.id} value={connection.id}>
          [{ENVIRONMENT_LABELS[connection.environment]}] {connection.name} — {connection.database}
        </option>
      ))}
    </Select>
  );
}

export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Busca por nome e envia o `id` — a spec proíbe digitar UUID à mão no formulário. */
export function OrgTypeahead({
  value,
  onChange,
  onResolved,
}: {
  value: string;
  onChange: (id: string) => void;
  onResolved?: (name: string | null) => void;
}): ReactNode {
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term);
  const organizations = useTargetOrganizations(debounced);

  const selected = useMemo(
    () => organizations.data?.find((organization) => organization.id === value) ?? null,
    [organizations.data, value],
  );

  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastResolvedName = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const name = selected?.name ?? null;
    if (lastResolvedName.current === name) return;
    lastResolvedName.current = name;
    onResolvedRef.current?.(name);
  }, [selected]);

  useEffect(() => {
    if (!organizations.isFetched || !value || debounced.trim()) return;
    if (!organizations.data?.some((organization) => organization.id === value)) {
      onChangeRef.current('');
    }
  }, [organizations.isFetched, organizations.data, value, debounced]);

  const selectValue = organizations.data?.some((organization) => organization.id === value) ? value : '';

  return (
    <div className="space-y-2">
      <Input
        placeholder="Buscar organização pelo nome…"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      <Select
        data-testid="org-select"
        value={selectValue}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">
          {organizations.isLoading ? 'Carregando…' : 'Selecione a organização'}
        </option>
        {organizations.data?.map((organization) => (
          <option key={organization.id} value={organization.id}>
            {organization.name}
            {organization.deletedAt ? ' (soft-deleted)' : ''}
          </option>
        ))}
      </Select>
      {organizations.isError && (
        <ErrorBlock
          message={
            organizations.error instanceof Error
              ? organizations.error.message
              : 'Falha ao listar organizações no banco alvo.'
          }
        />
      )}
      {!organizations.isLoading && !organizations.isError && (organizations.data?.length ?? 0) === 0 && (
        <p className="text-sm text-muted">
          Nenhuma organização neste banco. Confira a conexão ativa (o alvo E2E só tem Acme Test Org).
        </p>
      )}
      {selected && (
        <p className="font-mono text-[11px] text-muted">
          id: {selected.id} · ativa: {selected.isActive === false ? 'não' : 'sim'}
        </p>
      )}
    </div>
  );
}

export function BrandTypeahead({
  organizationId,
  value,
  onChange,
}: {
  organizationId?: string;
  value: string;
  onChange: (name: string) => void;
}): ReactNode {
  const brands = useTargetBrands(organizationId);

  if (!organizationId) {
    return <p className="text-sm text-muted">Selecione a organização primeiro.</p>;
  }

  const matched = brands.data?.some((brand) => brand.name === value);
  const selectValue = matched ? value : '';

  return (
    <div className="space-y-2">
      <Select
        data-testid="brand-select"
        value={selectValue}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{brands.isLoading ? 'Carregando…' : 'Selecione a marca'}</option>
        {brands.data?.map((brand) => (
          <option key={brand.id} value={brand.name}>
            {brand.name} ({brand.productCount} produtos)
          </option>
        ))}
      </Select>
      <Input
        placeholder="…ou digite o nome da marca (campo legado product_mappings.brand)"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

export function CategoryTypeahead({
  organizationId,
  value,
  onChange,
}: {
  organizationId?: string;
  value: string;
  onChange: (name: string) => void;
}): ReactNode {
  const categories = useTargetCategories(organizationId);

  if (!organizationId) {
    return <p className="text-sm text-muted">Selecione a organização primeiro.</p>;
  }

  const matched = categories.data?.some((category) => category.name === value);
  const selectValue = matched ? value : '';

  return (
    <div className="space-y-2">
      <Select
        data-testid="category-select"
        value={selectValue}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{categories.isLoading ? 'Carregando…' : 'Selecione a categoria'}</option>
        {categories.data?.map((category) => (
          <option key={category.id} value={category.name}>
            {category.name} ({category.productCount} produtos)
          </option>
        ))}
      </Select>
      <Input
        placeholder="…ou digite o nome da categoria"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

export function BusinessUnitSelect({
  organizationId,
  value,
  onChange,
}: {
  organizationId?: string;
  value: string;
  onChange: (businessUnitId: string) => void;
}): ReactNode {
  const units = useTargetBusinessUnits(organizationId);

  if (!organizationId) {
    return <p className="text-sm text-muted">Selecione a organização primeiro.</p>;
  }

  const selected = units.data?.find((unit) => unit.id === value);

  return (
    <div className="space-y-2">
      <Select
        data-testid="bu-select"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{units.isLoading ? 'Carregando…' : 'Selecione a Business Unit'}</option>
        {units.data?.map((unit) => (
          <option key={unit.id} value={unit.id}>
            {unit.name} ({unit.productCount} produtos
            {unit.siteCount > 0 ? `, ${unit.siteCount} sites` : ', sites irrestritos'}
            {unit.membershipStale ? ', stale' : ''})
          </option>
        ))}
      </Select>
      {selected && (
        <p className="text-xs text-muted">
          id: {selected.id} · slug: {selected.slug}
          {selected.membershipStale ? ' · membership_stale' : ''}
        </p>
      )}
    </div>
  );
}

export function RetailerSelect({
  organizationId,
  value,
  onChange,
  id,
}: {
  organizationId?: string;
  value: string;
  onChange: (siteConfigId: string) => void;
  id?: string;
}): ReactNode {
  const retailers = useTargetRetailers(organizationId);

  if (!organizationId) {
    return <p className="text-sm text-muted">Selecione a organização primeiro.</p>;
  }

  if (retailers.isLoading) {
    return <p className="text-sm text-muted">Carregando varejistas…</p>;
  }

  if (retailers.error) {
    return (
      <ErrorBlock
        message={
          retailers.error instanceof Error
            ? retailers.error.message
            : 'Falha ao listar varejistas no banco alvo.'
        }
      />
    );
  }

  const rows = retailers.data ?? [];
  if (rows.length === 0) {
    return <p className="text-sm text-muted">Nenhum watched site nesta organização.</p>;
  }

  return (
    <Select
      id={id}
      data-testid="retailer-select"
      value={rows.some((row) => row.siteConfigId === value) ? value : ''}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Selecione o varejista</option>
      {rows.map((retailer) => (
        <option key={retailer.siteConfigId} value={retailer.siteConfigId}>
          {retailer.name}
          {retailer.host ? ` · ${retailer.host}` : ''}
          {retailer.alias ? ` · ${retailer.alias}` : ''}
          {retailer.isEnabled === false ? ' · residual gold (Dashboard)' : ''}
        </option>
      ))}
    </Select>
  );
}

export function RetailerMultiselect({
  organizationId,
  value,
  onChange,
}: {
  organizationId?: string;
  value: string[];
  onChange: (siteConfigIds: string[]) => void;
}): ReactNode {
  const retailers = useTargetRetailers(organizationId);
  const selected = value ?? [];

  if (!organizationId) {
    return <p className="text-sm text-muted">Selecione a organização primeiro.</p>;
  }

  if (retailers.isLoading) {
    return <p className="text-sm text-muted">Carregando varejistas…</p>;
  }

  if (retailers.error) {
    return (
      <ErrorBlock
        message={
          retailers.error instanceof Error
            ? retailers.error.message
            : 'Falha ao listar varejistas no banco alvo.'
        }
      />
    );
  }

  const rows = retailers.data ?? [];
  if (rows.length === 0) {
    return <p className="text-sm text-muted">Nenhum watched site nesta organização.</p>;
  }

  return (
    <div className="space-y-2" data-testid="retailer-multiselect">
      <p className="text-xs text-muted">
        {selected.length} de {rows.length} selecionado(s)
      </p>
      <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border border-border p-3">
        {rows.map((retailer) => {
          const checked = selected.includes(retailer.siteConfigId);
          return (
            <label
              key={retailer.siteConfigId}
              className="flex cursor-pointer items-start gap-2 text-sm"
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={checked}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, retailer.siteConfigId]
                      : selected.filter((id) => id !== retailer.siteConfigId),
                  )
                }
              />
              <span>
                <span className="font-medium">{retailer.name}</span>
                {!retailer.isEnabled ? (
                  <span className="ml-1 text-xs text-muted">(desabilitado)</span>
                ) : null}
                <span className="mt-0.5 block font-mono text-[11px] text-muted">
                  {retailer.siteConfigId}
                  {retailer.host ? ` · ${retailer.host}` : ''}
                  {retailer.alias ? ` · alias: ${retailer.alias}` : ''}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      {selected.length > 0 ? (
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => onChange([])}>
          Limpar seleção
        </Button>
      ) : null}
    </div>
  );
}

export function UserTypeahead({
  value,
  onChange,
  onResolved,
}: {
  value: string;
  onChange: (id: string) => void;
  onResolved?: (email: string | null) => void;
}): ReactNode {
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term);
  const users = useTargetUsers(debounced);

  const selected = useMemo(
    () => users.data?.find((user) => user.id === value) ?? null,
    [users.data, value],
  );

  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastResolvedEmail = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const email = selected?.email ?? null;
    if (lastResolvedEmail.current === email) return;
    lastResolvedEmail.current = email;
    onResolvedRef.current?.(email);
  }, [selected]);

  useEffect(() => {
    if (!users.isFetched || !value || debounced.trim()) return;
    if (!users.data?.some((user) => user.id === value)) {
      onChangeRef.current('');
    }
  }, [users.isFetched, users.data, value, debounced]);

  const selectValue = users.data?.some((user) => user.id === value) ? value : '';

  return (
    <div className="space-y-2">
      <Input
        placeholder="Buscar usuário por e-mail ou nome…"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      <Select value={selectValue} onChange={(event) => onChange(event.target.value)}>
        <option value="">{users.isLoading ? 'Carregando…' : 'Selecione o usuário'}</option>
        {users.data?.map((user) => (
          <option key={user.id} value={user.id}>
            {user.email}
            {user.name ? ` — ${user.name}` : ''}
          </option>
        ))}
      </Select>
    </div>
  );
}

export function EnvironmentTokenHint({ environment }: { environment: Environment }): ReactNode {
  return <code className="font-mono text-content">{ENVIRONMENT_TOKENS[environment]}</code>;
}

export function LoadingBlock({ label = 'Carregando…' }: { label?: string }): ReactNode {
  return (
    <div className="flex items-center gap-2 p-6 text-sm text-muted">
      <Spinner />
      {label}
    </div>
  );
}

export function ErrorBlock({ message, className }: { message: string; className?: string }): ReactNode {
  return (
    <div
      role="alert"
      className={clsx(
        'rounded-md border border-severity-irreversible/60 bg-severity-irreversible/10 px-3 py-2 text-sm text-severity-irreversible',
        className,
      )}
    >
      {message}
    </div>
  );
}

export function StatusBlock({ message, className }: { message: string; className?: string }): ReactNode {
  return (
    <div
      role="status"
      className={clsx(
        'rounded-md border border-severity-reversible/60 bg-severity-reversible/10 px-3 py-2 text-sm text-severity-reversible',
        className,
      )}
    >
      {message}
    </div>
  );
}
